<template>
    <modal
        :active="active"
        @closed="$emit('closed', true)"
        :title="title()"
        :large="true"
    >
        <div class="columns is-tablet mt-1">
            <form-field class="column" :error="error.in_stock">
                <InputCheckbox
                    v-model="form.in_stock"
                    label="På lager"
                    :inline="true"
                    :true-value="true"
                    :false-value="false"
                />
            </form-field>
        </div>

        <div class="columns is-tablet mt-1">
            <form-field
                class="column"
                label="EU Automation leveringstid"
                help-text="Angiv hele dage (eksempel: 3).<br /> Bruges kun til EU Automation Export."
                :error="error.euautomation_days"
            >
                <input-decimal
                    v-model="form.euautomation_days"
                    :error="error.euautomation_days"
                />
            </form-field>
        </div>

        <div class="tabs">
            <ul>
                <li
                    v-for="locale in listLocales"
                    :key="locale.id"
                    :class="{ 'is-active': locale_id === locale.id }"
                    @click.prevent="locale_id = locale.id"
                >
                    <a>
                        {{ locale.name }}
                        <span
                            class="icon"
                            v-if="
                                !form.localizations?.find(
                                    (localization) =>
                                        localization.locale_id === locale.id &&
                                        localization.key === 'name'
                                )
                            "
                        >
                            <i class="fas fa-exclamation-triangle"></i>
                        </span>
                    </a>
                </li>
            </ul>
        </div>

        <div class="columns is-tablet mb-1">
            <form-field
                class="column"
                :error="error.localizations"
                label="Leveringstid"
            >
                <input-text
                    v-model="name"
                    :error="error.name"
                    @input="nameChanged"
                />
            </form-field>
        </div>

        <template v-slot:footer>
            <div class="footer-spacer">
                <div>
                    <button
                        class="button is-danger is-flex-left"
                        @click="reset"
                    >
                        Nulstil
                    </button>
                    <button class="button is-default" @click="findItems">
                        Find varer
                    </button>
                    <button
                        class="button is-default"
                        @click="findManufacturers"
                    >
                        Find fabrikanter
                    </button>
                    <button class="button is-default" @click="$emit('closed')">
                        Annuller
                    </button>
                    <button class="button is-primary" @click="submit">
                        {{ saveButtonText }}
                    </button>
                </div>

                <button class="button is-danger" @click="remove" v-if="isEdit">
                    Slet
                </button>
            </div>
        </template>
    </modal>
</template>

<script>
import Modal from "@elems/modal";
import {
    FormField,
    InputText,
    InputCheckbox,
    InputSelect,
    InputDecimal,
} from "@elems/form";
import { mapGetters } from "vuex";

export default {
    components: {
        InputCheckbox,
        Modal,
        FormField,
        InputText,
        InputSelect,
        InputDecimal,
    },
    props: {
        active: {
            type: Boolean,
            default: false,
        },
        deliveryType: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            form: {
                localizations: [],
                in_stock: false,
                euautomation_days: 123,
            },
            error: [],
            locale_id: null,
            name: "",
        };
    },
    computed: {
        isEdit() {
            return this.deliveryType !== null;
        },
        saveButtonText() {
            return this.isEdit ? "Gem" : "Opret";
        },
        ...mapGetters(["listLocales"]),
    },
    methods: {
        findItems() {
            this.$router.push({
                name: "item.index",
                query: {
                    "delivery-type": this.deliveryType.id,
                },
            });
        },
        findManufacturers() {
            this.$router.push({
                path: "/settings/inventory",
                query: {
                    tab: "manufacturers",
                    "delivery-type": this.deliveryType.id,
                },
            });
        },
        title() {
            return this.isEdit ? "Rediger leveringstid" : "Opret leveringstid";
        },
        submit() {
            return this.isEdit ? this.update() : this.create();
        },
        create() {
            window.axios
                .post("/api/delivery-types", this.form)
                .then(({ data }) => {
                    this.$emit("created", data);
                    this.$store.dispatch(
                        "setSuccess",
                        "Leveringstid blev oprettet"
                    );
                })
                .catch((error) => {
                    if (error.response && error.response.status === 422) {
                        this.error = error.response.data.errors;
                    }
                });
        },
        update() {
            window.axios
                .put("/api/delivery-types/" + this.deliveryType.id, this.form)
                .then(({ data }) => {
                    this.$emit("updated", data);
                    this.$store.dispatch(
                        "setSuccess",
                        "Leveringstid blev opdateret"
                    );
                })
                .catch((error) => {
                    if (error.response && error.response.status === 422) {
                        this.error = error.response.data.errors;
                    }
                });
        },
        reset() {
            this.locale_id = this.listLocales[0].id;

            if (this.isEdit) {
                this.form = {
                    in_stock: this.deliveryType.in_stock,
                    euautomation_days: this.deliveryType.euautomation_days,
                    localizations: this.deliveryType.localizations.map(
                        (localization) => {
                            return {
                                locale_id: localization.locale_id,
                                key: localization.key,
                                value: localization.value,
                            };
                        }
                    ),
                };

                this.updateName();
            } else {
                this.form = {
                    in_stock: false,
                    euautomation_days: 0,
                    localizations: [],
                };

                this.name = "";
            }
            this.error = [];
        },
        async remove() {
            await window.axios
                .get("/api/delivery-types/" + this.deliveryType.id + "/in-use")
                .then(({ data }) => {
                    if (data) {
                        this.error = {
                            localizations: [
                                "Leveringstiden bruges på en eller flere fabrikanter og kan ikke slettes.",
                            ],
                        };
                    }
                })
                .catch((error) => {
                    console.error(error);
                });

            if (Object.keys(this.error).length > 0) {
                return;
            }

            if (!confirm("Er du sikker på du vil slette " + this.name + "?")) {
                return;
            }

            window.axios
                .delete("/api/delivery-types/" + this.deliveryType.id)
                .then(() => {
                    this.$emit("deleted", this.deliveryType);
                    this.$store.dispatch(
                        "setSuccess",
                        "Leveringstid blev slettet"
                    );
                })
                .catch((error) => {
                    this.$store.dispatch("setError", "Der skete en fejl");
                });
        },
        updateName() {
            const name = this.form.localizations.find(
                (localization) =>
                    localization.locale_id === parseInt(this.locale_id) &&
                    localization.key === "name"
            );
            this.name = name ? name.value : "";
        },
        nameChanged() {
            if (this.name.trim() === "") {
                this.form.localizations = this.form.localizations.filter(
                    (localization) =>
                        !(
                            localization.locale_id ===
                                parseInt(this.locale_id) &&
                            localization.key === "name"
                        )
                );
                return;
            }

            const name = this.form.localizations.find(
                (localization) =>
                    localization.locale_id === parseInt(this.locale_id) &&
                    localization.key === "name"
            );
            if (name) {
                name.value = this.name;
            } else {
                this.form.localizations.push({
                    locale_id: parseInt(this.locale_id),
                    key: "name",
                    value: this.name,
                });
            }
        },
    },
    watch: {
        active() {
            if (this.active) {
                this.reset();
            }
        },
        locale_id() {
            this.updateName();
        },
    },
};
</script>

<style lang="scss" scoped>
.footer-spacer {
    width: 100%;
    display: flex;
    flex-direction: row-reverse;
    justify-content: space-between;
}

.customer-details {
    h4 {
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 1px solid #bbb;
        margin-bottom: 25px;
        cursor: pointer;

        i {
            transition: transform 0.3s ease-in-out;
        }
    }

    .column {
        display: none;
    }

    &.open {
        h4 i {
            transform: rotate(0.5turn);
        }

        .column {
            display: block;
        }
    }
}

.column {
    padding: 0 0.75rem;
}
</style>
